import * as SelectPrimitive from "@radix-ui/react-select";
import { type VariantProps, cva } from "class-variance-authority";
import { Check, ChevronDown, ChevronUp } from "lucide-react";
import * as React from "react";
import { cn } from "../lib/cn";

/**
 * Select — the single source of truth for every "pick one of N" control.
 *
 * Use this instead of a native `<select>`: the native one paints the OS
 * dropdown (blue highlight, system font, system radius), which is the one
 * surface in Ando the design system cannot reach. Same chrome tokens as the
 * DropdownMenu primitive, so a select and a menu read as one language.
 *
 * ```tsx
 * <Select value={role} onValueChange={setRole}>
 *   <SelectTrigger ariaLabel={t`Ruolo`} className="w-40" />
 *   <SelectContent>
 *     <SelectItem value="all">{t`Tutti i ruoli`}</SelectItem>
 *     <SelectItem value="admin">Admin</SelectItem>
 *   </SelectContent>
 * </Select>
 * ```
 *
 * Not for: multi-select (use checkboxes), 2-3 mutually exclusive options that
 * fit inline (a segmented control reads faster), or an action menu (that is
 * DropdownMenu — a menu runs commands, a select holds a value).
 */

const Select = SelectPrimitive.Root;
const SelectGroup = SelectPrimitive.Group;
const SelectValue = SelectPrimitive.Value;

// Height/padding follow the Button ladder so a select sitting in a filter bar
// lines up with the buttons next to it. Type stays on the preset scale (`xs`
// = 12px) at every size — a filter control never needs its own type ramp.
const selectTriggerVariants = cva(
  [
    "group flex w-full min-w-32 items-center justify-between gap-2 whitespace-nowrap",
    "rounded-md border border-hairline bg-white/45 text-ink-1 text-xs",
    "transition-colors duration-200 ease-out hover:bg-white/60",
    // No focus signal at all, by design decision: the control looks identical
    // focused and at rest. `outline-none` is what suppresses the browser's own
    // ring — without it the native one would show up instead.
    "focus-visible:outline-none",
    "disabled:pointer-events-none disabled:opacity-50",
    "data-[placeholder]:text-ink-3",
  ].join(" "),
  {
    variants: {
      size: {
        sm: "h-7 px-2",
        md: "h-8 px-2.5",
        lg: "h-10 px-3",
      },
    },
    defaultVariants: { size: "md" },
  },
);

export interface SelectTriggerProps
  extends React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>,
    VariantProps<typeof selectTriggerVariants> {
  /** Accessible name. Required when no visible label points at the trigger. */
  ariaLabel?: string;
  /** Shown while the value is empty. */
  placeholder?: string;
}

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  SelectTriggerProps
>(({ className, size, ariaLabel, placeholder, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    aria-label={ariaLabel}
    className={cn(selectTriggerVariants({ size }), className)}
    {...props}
  >
    {children ?? <SelectValue placeholder={placeholder} />}
    <SelectPrimitive.Icon asChild>
      <ChevronDown
        aria-hidden
        className={cn(
          "size-3.5 shrink-0 text-ink-3",
          // `data-state` lives on the TRIGGER, not on the icon — hence the
          // group variant. On the icon itself the class would never fire.
          "transition-transform duration-200 ease-out group-data-[state=open]:rotate-180",
        )}
      />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
));
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn("flex cursor-default items-center justify-center py-1 text-ink-3", className)}
    {...props}
  >
    <ChevronUp className="size-3.5" aria-hidden />
  </SelectPrimitive.ScrollUpButton>
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn("flex cursor-default items-center justify-center py-1 text-ink-3", className)}
    {...props}
  >
    <ChevronDown className="size-3.5" aria-hidden />
  </SelectPrimitive.ScrollDownButton>
));
SelectScrollDownButton.displayName = SelectPrimitive.ScrollDownButton.displayName;

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = "popper", ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      position={position}
      sideOffset={4}
      className={cn(
        "z-50 max-h-72 min-w-[8rem] overflow-hidden rounded-md border border-hairline bg-card p-1 text-card-foreground shadow-lg",
        "data-[state=closed]:animate-fade-out data-[state=open]:animate-fade-in",
        className,
      )}
      {...props}
    >
      <SelectScrollUpButton />
      {/* The menu takes the trigger width so the two read as one control. The
          0.5 gap is DESIGN.md §4: two filled rows side by side would weld. */}
      <SelectPrimitive.Viewport className="flex w-full min-w-[var(--radix-select-trigger-width)] flex-col gap-0.5">
        {children}
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
));
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn(
      "px-2 py-1.5 font-medium text-[11px] text-ink-3 uppercase tracking-[0.05em]",
      className,
    )}
    {...props}
  />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

export interface SelectItemProps
  extends React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item> {
  /**
   * Show the check on the right. Turn it off where the selected row is already
   * unmistakable — a short filter list, where the state fill alone reads
   * cleaner and the check is one mark too many.
   */
  indicator?: boolean;
}

// The check sits RIGHT: that is where a select puts it (a menu is the one that
// marks left).
//
// The selected row is a SOLID orange fill with white text, not a veil. Orange is
// legitimate here even though §3 keeps it out of the chrome: the menu is
// transient, so it never spends the "90% neutral" budget of a resting screen,
// and it marks the current value — which is what orange means in Ando. A solid
// fill also separates from the warm-grey hover far better than any two greys
// could (§4). The stacked `checked:focus` keeps selection winning when the open
// menu lands its focus on the selected row.
//
// A SANDY veil, not a solid fill: brand orange at 14%, written as
// `ando-orange/[0.14]` and never as `orange-soft/NN` — the modifier REPLACES the
// alpha on an rgba() token, so `orange-soft/40` is 40% orange, not a veil
// (DESIGN.md §2). 14% and not orange-soft's own 12% so it separates from the
// warm-grey hover (`cream-deep`) by warmth and not only by weight (§4).
//
// The label is `warning-foreground`, the DARK orange, not the brand one: on this
// veil the bright `ando-orange` sits at 2.3:1 and white at 1.1:1 (a solid fill
// would be needed for white, and even the darker hover token only reaches
// 3.3:1) — all under the 4.5:1 that TEXT owes. The dark orange clears 6:1 and
// still reads unmistakably orange. It is the same correction the Badge already
// carries on its `accent` variant.
//
// Note the difference from a decorative glyph: an icon that merely repeats what
// the adjacent text already says is exempt from the contrast floor. This is the
// option label itself, so it is not.
const SelectItem = React.forwardRef<React.ElementRef<typeof SelectPrimitive.Item>, SelectItemProps>(
  ({ className, children, indicator = true, ...props }, ref) => (
    <SelectPrimitive.Item
      ref={ref}
      className={cn(
        "relative flex w-full cursor-default select-none items-center rounded-sm px-2 py-1.5 text-xs outline-none transition-colors",
        indicator && "pr-8",
        "focus:bg-cream-deep focus:text-ink-1 data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        "data-[state=checked]:bg-ando-orange/[0.14] data-[state=checked]:font-medium data-[state=checked]:text-warning-foreground",
        "data-[state=checked]:focus:bg-ando-orange/[0.14]",
        className,
      )}
      {...props}
    >
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
      {indicator ? (
        <span className="absolute right-2 flex size-3.5 items-center justify-center">
          <SelectPrimitive.ItemIndicator>
            <Check className="size-3.5 text-warning-foreground" aria-hidden />
          </SelectPrimitive.ItemIndicator>
        </span>
      ) : null}
    </SelectPrimitive.Item>
  ),
);
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn("-mx-1 my-1 h-px bg-hairline", className)}
    {...props}
  />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
  selectTriggerVariants,
};
